'use client';
import React, { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import BlogService from '@/app/services/blog';
import { BASE_URL } from '@/app/contants/url';
import ArticleError from '@/app/components/ui-elements/error/ArticleError';
import { ChevronLeft, ChevronRight } from "lucide-react";
import NewsSkeleton from '@/app/components/ui-elements/skeleton/NewsSkeleton';

export default function NewsArticles() {
    const t = useTranslations('News');
    const locale = useLocale();

    const [data, setData] = useState<any[]>([])
    const [error, setError] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const [meta, setMeta] = useState<any>(null)
    const [page, setPage] = useState<number>(1)

    const [largeArticle, setLargeArticle] = useState<any>(null)
    const [columnTwoArticles, setColumnTwoArticles] = useState<any[]>([])
    const [columnThreeArticles, setColumnThreeArticles] = useState<any[]>([])

    async function getCategoriesList() {
        try {
            const body = {
                no_pagination: 1
            }
            const result: any = await BlogService.getCategories(body);
            if (result?.data?.success) {
                setCategories(result?.data?.data)
            }
        } catch (error: any) {
        } finally {
        }
    }

    async function getList() {
        setIsLoading(true)
        try {
            const body: any = {
                page: page,
                page_size: 16,
                language: locale,
                sort: 'desc'
            }
            if (selectedCategory) { body.category_id = selectedCategory }
            const result: any = await BlogService.getList(body);
            if (result?.data?.success) {
                setData(result?.data?.data);
                //setLargeArticle(result?.data?.data[2]);
                setColumnTwoArticles(result?.data?.data.slice(2, result?.data?.data?.length));
                //setColumnThreeArticles(result?.data?.data.slice(5, result?.data?.data?.length));
                setMeta(result?.data?.meta)
                setError(false)
            } else {
                setError(true)
            }
        } catch (error: any) {
            setError(true)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { getCategoriesList() }, [])
    useEffect(() => { getList() }, [page, selectedCategory])

    return (
        <>
            {isLoading ? (< NewsSkeleton />) : (
                <>
                    {error ? (
                        <ArticleError errorMessage={t('articles_error')} displayButton={false} />
                    ) : (
                        <div className='mt-6 mb-2 px-4 md:px-20 text-start'>

                            {categories?.length > 0 && (
                                <>
                                    <div className="flex gap-3 mb-3 flex-wrap">
                                        <button onClick={() => { setSelectedCategory(null) }}><CategoryPill icon={<></>} label="All" active={!selectedCategory} /></button>
                                        {categories?.map((cat: any) => (
                                            <button onClick={() => { setSelectedCategory(cat?.id) }}><CategoryPill icon={<></>} label={`${cat[`name_${locale}`]}`} active={selectedCategory == cat?.id} /></button>
                                        ))}
                                    </div>
                                </>
                            )}

                            {data?.length <= 0 ? (
                                <>
                                    <div className="text-center mt-2 text-gray-400">
                                        {t('articles_no_data')}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className='flex flex-col md:flex-row justify-start pb-10 gap-4 border-b-1 border-slate-100'>
                                        <div className='flex-1'>
                                            <Link href={`/blog/${data[0]?.slug}`}>
                                                <div
                                                    className='h-[250px] md:h-[450px] w-full bg-center bg-no-repeat bg-cover mb-1'
                                                    style={{ backgroundImage: `url(${BASE_URL}storage/${data[0]?.cover_path})` }}
                                                >
                                                </div>
                                            </Link>
                                            {data[0]?.links?.map((ln: any, index: number) => (
                                                <React.Fragment key={ln?.link || index}>
                                                    <Link
                                                        href={ln?.link ?? ""}
                                                        target='_blank'
                                                        className='text-gray-400 underline'
                                                    >
                                                        {ln?.title}
                                                    </Link>

                                                    {index < data[0]?.links?.length - 1 && (
                                                        <span className="text-gray-400 mr-1">, </span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            <h2 className='text-md md:text-xl font-regular mt-2'>
                                                {data[0]?.title}
                                            </h2>
                                        </div>
                                        {data?.length > 1 && (
                                            <div className='flex-1'>
                                                <Link href={`/blog/${data[1]?.slug}`}>
                                                    <div
                                                        className='h-[250px] md:h-[450px] w-full bg-center bg-no-repeat bg-cover'
                                                        style={{ backgroundImage: `url(${BASE_URL}storage/${data[1]?.cover_path})` }}
                                                    >
                                                    </div>
                                                </Link>
                                                {data[1]?.links?.map((ln: any, index: number) => (
                                                    <React.Fragment key={ln?.link || index}>
                                                        <Link
                                                            href={ln?.link}
                                                            target='_blank'
                                                            className='text-gray-400 underline'
                                                        >
                                                            {ln?.title}
                                                        </Link>

                                                        {index < data[1]?.links?.length - 1 && (
                                                            <span className="text-gray-400 mr-1">, </span>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                                <h2 className='text-md md:text-xl font-regular mt-2'>
                                                    {data[1]?.title}
                                                </h2>
                                            </div>
                                        )}
                                    </div>
                                    {data?.length > 2 && (
                                        <div className='flex flex-col md:grid md:grid-cols-3 justify-start pb-4 gap-8 mt-0 md:mt-16'>
                                                {columnTwoArticles.map((item: any, mapIndex: number) => (
                                                    <div key={mapIndex} className='flex-1'>
                                                        <Link href={`/blog/${item?.slug}`}>
                                                            <div
                                                                className='h-[250px] md:h-[300px] w-full bg-center bg-no-repeat bg-cover'
                                                                style={{ backgroundImage: `url(${BASE_URL}storage/${item?.cover_path})` }}
                                                            >
                                                            </div>
                                                        </Link>
                                                        {item?.links?.map((ln: any, index: number, array: any[]) => (
                                                            <React.Fragment key={ln?.link || index}>
                                                                <Link href={ln?.link} target='_blank' className='text-gray-400 underline'>{ln?.title}</Link>
                                                                {index < array.length - 1 && (<span className="text-gray-400 mr-1">, </span>)}
                                                            </React.Fragment>
                                                        ))}

                                                        <h2 className='text-md md:text-xl font-regular mt-2'>{item?.title}</h2>

                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                    <div className='mt-8 pb-4'>
                                        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8'>
                                            {data.slice(18).map((item: any, mapIndex: number) => (
                                                <div key={mapIndex} className='col-span-1'>
                                                    <Link href={`/blog/${item?.slug}`}>
                                                        <div
                                                            className='h-[250px] md:h-[300px] w-full bg-center bg-no-repeat bg-cover mb-2'
                                                            style={{ backgroundImage: `url(${BASE_URL}storage/${item?.cover_path})` }}
                                                        >
                                                        </div>
                                                    </Link>

                                                    {item?.links?.map((ln: any, index: number, array: any[]) => (
                                                        <React.Fragment key={ln?.link || index}>
                                                            <Link href={ln?.link} target='_blank' className='text-gray-400 underline text-sm'>{ln?.title}</Link>
                                                            {index < array.length - 1 && (<span className="text-gray-400 mr-1 text-sm">, </span>)}
                                                        </React.Fragment>
                                                    ))}

                                                    <h2 className='text-md md:text-xl font-regular mt-2'>
                                                        {item?.title}
                                                    </h2>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {meta && (
                                        <div className="flex items-center justify-center mt-8 gap-6">
                                            <button
                                                onClick={() => setPage(page - 1)}
                                                className="p-2 text-gray-500 hover:text-white transition-colors disabled:opacity-20"
                                                disabled={page === 1}
                                            >
                                                <ChevronLeft size={40} strokeWidth={1} />
                                            </button>

                                            {/* Dots Navigation */}
                                            <div className="flex gap-3">
                                                {Array.from({ length: meta?.last_page }, (_, i) => {
                                                    const pageNumber = i + 1;
                                                    return (
                                                        <div
                                                            key={pageNumber}
                                                            onClick={() => setPage(pageNumber)}
                                                            className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all duration-300 ${pageNumber === meta?.current_page
                                                                ? "bg-primary scale-125"
                                                                : "bg-gray-400 hover:bg-gray-700"
                                                                }`}
                                                        />
                                                    );
                                                })}
                                            </div>

                                            <button
                                                onClick={() => setPage(page + 1)}
                                                className="p-2 text-gray-500 hover:text-white transition-colors disabled:opacity-20"
                                                disabled={page === meta?.last_page}
                                            >
                                                <ChevronRight size={40} strokeWidth={1} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}


function CategoryPill({ icon, label, active = false }: { icon: any, label: string, active?: boolean }) {
    return (
        <button className={`flex items-center gap-2 px-5 py-1 rounded-full border text-sm font-medium transition-all
      ${active ? "bg-dark border-dark text-white" : "border-gray-800 hover:border-gray-600 bg-white/5"}`}>
            {label} {icon}
        </button>
    );
}